import React, { useState } from 'react';
import '../registration/searchPopupDesign.css';

const SearchPopup = () => {
  const searchParams = new URLSearchParams(window.location.search);
  const type = searchParams.get('type');
  const inputTarget = searchParams.get('inputTarget');
  const apiUrlBase = decodeURIComponent(searchParams.get('url'));

  const [query, setQuery] = useState('');
  const [sortBy, setSortBy] = useState('Sort by Name');
  const [results, setResults] = useState([]);

  const token = localStorage.getItem('jwt');

  if (type === 'InsurancePatient') {
    return (
      <div className="popup-overlay">
        <div className="popup-content">
          <h2>❌ Unsupported Search Type</h2>
          <p>This search type has moved to a new page.</p>
          <button className="close-button" onClick={() => window.close()}>
            Close
          </button>
        </div>
      </div>
    );
  }

  const fetchData = async () => {
    try {
      let url = apiUrlBase;

      if (query.trim()) {
        const connector = url.includes('?') ? '&' : '?';
        url += `${connector}query=${encodeURIComponent(query)}`;
      }

      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();

      let filtered = Array.isArray(data) ? data : [];

      if (type === 'Doctor') {
        const allowedSpecialties = [
          'Physical Therapist',
          'Massage Therapist',
          'PT Staff',
          'doctor/physical therapist',
          'massager',
        ];

        filtered = filtered.filter((doctor) => {
          if (!doctor.specialty) return true;

          return allowedSpecialties.some(
            (allowed) =>
              String(doctor.specialty).toLowerCase() === allowed.toLowerCase()
          );
        });
      }

      setResults(sortResults(filtered, sortBy));
    } catch (error) {
      console.error('❌ Search error:', error);
      alert('Failed to fetch search results.');
    }
  };

  const extractId = (result) => {
    if (!result) return undefined;

    if (type === 'Office') {
      return result.officeId ?? result.office_id ?? result.id;
    }

    if (type === 'Doctor') {
      return result.doctorId ?? result.doctor_id ?? result.id;
    }

    if (type === 'Patient') {
      return result.patientId ?? result.patient_id ?? result.id;
    }

    if (type === 'Insurance') {
      return result.insId ?? result.insid ?? result.ins_id ?? result.id;
    }

    return result.id ?? result._id;
  };

  const getPersonName = (result) => {
    if (!result) return '';

    const first =
      result.firstName ||
      result.firstname ||
      result.first_name ||
      result.FirstName ||
      '';

    const last =
      result.lastName ||
      result.lastname ||
      result.last_name ||
      result.LastName ||
      '';

    return `${first} ${last}`.trim();
  };

  const getDisplayName = (result) => {
    if (!result) return 'Unnamed';

    if (type === 'Insurance') {
      return (
        result.insuranceName ||
        result.insName ||
        result.insCode ||
        `Insurance ID ${extractId(result)}`
      );
    }

    if (type === 'Patient') {
      return (
        getPersonName(result) ||
        result.username ||
        result.userName ||
        result.name ||
        `Patient #${extractId(result)}`
      );
    }

    if (type === 'Doctor') {
      const doctorName =
        getPersonName(result) ||
        result.name ||
        result.username ||
        result.userName ||
        result.user_name ||
        `Doctor #${extractId(result)}`;

      return `Dr. ${doctorName}${
        result.specialty ? ` (${result.specialty})` : ''
      }`;
    }

    if (type === 'Office') {
      return (
        result.officeName ||
        result.name ||
        result.title ||
        `Office #${extractId(result)}`
      );
    }

    return (
      result.name ||
      result.title ||
      result.username ||
      `Record #${extractId(result)}`
    );
  };

  const sortResults = (items, selectedSort) => {
    return items.slice().sort((a, b) => {
      if (selectedSort === 'Sort by ID') {
        return String(extractId(a) || '').localeCompare(
          String(extractId(b) || '')
        );
      }

      if (selectedSort === 'Sort by Name') {
        return getDisplayName(a).localeCompare(getDisplayName(b));
      }

      if (selectedSort === 'Sort by Username') {
        return (a.username || a.userName || '').localeCompare(
          b.username || b.userName || ''
        );
      }

      return 0;
    });
  };

  const handleSearch = () => {
    if (!query.trim()) {
      alert('Please enter a search term.');
      return;
    }

    fetchData();
  };

  const handleSelect = (result) => {
    const id = extractId(result);
    const name = getDisplayName(result);

    if (!id) {
      alert('❌ No valid ID found for this selection.');
      return;
    }

    window.opener?.postMessage(
      {
        inputTarget,
        value: id,
        displayName: name,
      },
      '*'
    );

    window.close();
  };

  const titleMap = {
    Insurance: 'Search Insurance',
    Patient: 'Search Patient',
    Office: 'Search Office',
    Doctor: 'Search Doctor',
  };

  return (
    <div className="popup-overlay">
      <div className="popup-content">
        <h2>{titleMap[type] || `Search ${type}`}</h2>

        <input
          type="text"
          placeholder="Search by name or ID"
          className="search-input"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              handleSearch();
            }
          }}
        />

        <select
          value={sortBy}
          onChange={(e) => {
            setSortBy(e.target.value);
            setResults((prev) => sortResults(prev, e.target.value));
          }}
          style={{ marginBottom: '10px', padding: '10px', width: '85%' }}
        >
          <option>Sort by ID</option>
          <option>Sort by Name</option>
          <option>Sort by Username</option>
        </select>

        <button className="search-button" onClick={handleSearch}>
          Search
        </button>

        <ul className="results-list">
          {results.length === 0 && <li>No results found.</li>}

          {results.map((result, index) => (
            <li key={index} onClick={() => handleSelect(result)}>
              {getDisplayName(result)}
            </li>
          ))}
        </ul>

        <button className="close-button" onClick={() => window.close()}>
          Close
        </button>
      </div>
    </div>
  );
};

export default SearchPopup;